import React, { useMemo, useRef, useState } from "react";

import CountsTable from "../../components/CountsTable";
import ExportMenu from "../../components/ExportMenu";

import TrafficChart from "../../components/charts/TrafficChart";
import ChartExportMenu from "../../components/charts/ChartExportMenu";

import { addMonths, monthIndexFromName } from "../../lib/dates";
import { goSightings } from "../../lib/links";
import { toQueryCam } from "../../lib/cameras";
import { exportTable } from "../../lib/csv";

const navBtn = {
  padding: "4px 10px",
  borderRadius: 8,
  border: "1px solid #1f2834",
  background: "#151b24",
  color: "white",
  lineHeight: 1,
  fontSize: 16,
  cursor: "pointer",
};

export default function YearCard({ year, setYear, visible, rows, plate, setMonth }) {
  const [stacked, setStacked] = useState(true);
  const chartRef = useRef(null);

  const card = { background: "#0f141c", border: "1px solid #1f2834", borderRadius: 16, padding: 16 };

  // ---- chart data
  const chartData = useMemo(() => {
    const src = Array.isArray(rows) ? rows : [];
    return src.map((r) => ({
      label: r.month,
      k41: Number(r["41"] || 0),
      chel: Number(r["Cheltenham"] || 0),
      pool: Number(r["Pool"] || 0),
    }));
  }, [rows]);

  const visibleRowTotalFor = (r) =>
    (visible.cam41 ? Number(r["41"] ?? 0) : 0) +
    (visible.chel ? Number(r["Cheltenham"] ?? 0) : 0) +
    (visible.pool ? Number(r["Pool"] ?? 0) : 0);

  const buildExport = () => {
    const src = (rows || []).filter((r) => visibleRowTotalFor(r) > 0);
    const headers = [
      "month",
      ...(visible.cam41 ? ["41"] : []),
      ...(visible.chel ? ["Cheltenham"] : []),
      ...(visible.pool ? ["Pool"] : []),
      "Total",
    ];
    const out = src.map((r) => ({
      month: r.month,
      ...(visible.cam41 ? { "41": r["41"] } : {}),
      ...(visible.chel ? { Cheltenham: r["Cheltenham"] } : {}),
      ...(visible.pool ? { Pool: r["Pool"] } : {}),
      Total: visibleRowTotalFor(r),
    }));
    return { headers, rows: out, title: `Traffic This Year (${year})`, filename: `year_${year}` };
  };

  return (
    <section style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h2 style={{ margin: 0, color: "#9ec3ff", fontSize: 18 }}>Traffic This Year ({year})</h2>
        <ExportMenu
          onExport={(t) => {
            const { headers, rows, title, filename } = buildExport();
            exportTable(t, headers, rows, title, filename);
          }}
        />
      </div>

      {/* Year navigation */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <div style={{ color: "#a7b3c4", fontSize: 12 }}>Year</div>
        <button
          style={navBtn}
          aria-label="Previous year"
          onClick={() => setYear((y) => (typeof y === "number" ? y - 1 : year - 1))}
        >
          ‹
        </button>
        <input
          type="number"
          value={year}
          onChange={(e) => {
            const n = parseInt(e.target.value || `${new Date().getFullYear()}`, 10);
            setYear(Number.isFinite(n) ? n : year);
          }}
          style={{ width: 100, padding: "6px 10px", borderRadius: 8, border: "1px solid #1f2834", background: "#0f141c", color: "white" }}
        />
        <button
          style={navBtn}
          aria-label="Next year"
          onClick={() => setYear((y) => (typeof y === "number" ? y + 1 : year + 1))}
        >
          ›
        </button>
        <button
          onClick={() => setYear(new Date().getFullYear())}
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #1f2834", background: "#151b24", color: "white" }}
        >
          This Year
        </button>
      </div>

      {/* Table */}
      <CountsTable
        rows={rows}
        firstColKey="month"
        firstColHeader="Month / Camera"
        visible={visible}
        onFirstCol={(r) => {
          const idx = monthIndexFromName(r.month);
          setMonth(new Date(year, idx, 1)); // update Month (no redirect)
        }}
        onCell={(r, key) => {
          const idx = monthIndexFromName(r.month);
          const start = new Date(year, idx, 1);
          const end = addMonths(start, 1);
          const cams =
            key === "Total"
              ? [
                  ...(visible.cam41 ? ["41"] : []),
                  ...(visible.chel ? ["cheltenham"] : []),
                  ...(visible.pool ? ["pool"] : []),
                ]
              : [toQueryCam(key)];
          setMonth(start);
          goSightings(start, end, cams, plate);
        }}
      />

      {/* Chart header: stacked/grouped + export */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
        <div style={{ color: "#a7b3c4", fontSize: 12 }}>Chart</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            value={stacked ? "stacked" : "grouped"}
            onChange={(e) => setStacked(e.target.value === "stacked")}
            style={{ padding: "4px 8px", background: "#0f141c", color: "white", borderRadius: 6, border: "1px solid #1f2834" }}
          >
            <option value="stacked">Stacked</option>
            <option value="grouped">Grouped</option>
          </select>
          <ChartExportMenu targetRef={chartRef} filename={`year_${year}`} />
        </div>
      </div>

      <div ref={chartRef}>
        <TrafficChart data={chartData} visible={visible} stacked={stacked} height={180} />
      </div>
    </section>
  );
}
